import { useEffect, useState } from "react";
import styled from "styled-components";
import { queryClient } from "..";
import { useAppSelector } from "../app/hooks";
import { locationState } from "../app/reducers/location-slice";
import { ContainedYellowButton } from "../components/button/ContainedYellowButton";
import { LocationFormDialog } from "../components/dialog/LocationFormDialog";
import { PageController } from "../components/paging/PageController";
import { LocationListTable } from "../components/table/LocationListTable";
import { IconTextField } from "../components/text-field/IconTextField";
import {
  LocationQueryKey,
  useGetLocationByIdQuery,
  useGetLocationPageQuery,
} from "../query/location-query";
import theme from "../style/theme";
import { useDebouncedCallback } from "../utils/custom-hook/use-debounced-callback";
import useDidMountEffect from "../utils/custom-hook/use-did-mount-effect";
import EventBus, {EventTypes} from "../utils/eventbus/event-bus";

const LOCATION_SEARCH_PLACEHOLDER = "도로명 또는 지번 주소 입력";

const LocationView = () => {
  const { selectedLocation, locationPage } = useAppSelector(locationState);
  const [pagePayload, setPagePayload] = useState<any>({});
  const [idPayload, setIdPayload] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  useGetLocationByIdQuery(idPayload);
  useGetLocationPageQuery(pagePayload);

  const setSearchPayload = async (page: number = 0) => {
    const newPayload: any = {
      page,
    };
    if (keyword !== "") {
      newPayload["keyword"] = keyword;
    }
    await setPagePayload(newPayload);
    await queryClient.invalidateQueries(LocationQueryKey.Page);
  };

  const debouncedSearchPayload = useDebouncedCallback(
    async (page: number = 0) => setSearchPayload(page)
  );

  const handleClickLocationDialogOpen = () => {
    setLocationDialogOpen(true);
  };

  const handleClickLocationDialogClose = () => {
    setLocationDialogOpen(false);
  };

  const handleChangeTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSearchKeyword = event.target.value;
    setKeyword(newSearchKeyword);
  };

  const handleChangePage = (selectedItem: { selected: number }) => {
    const page = selectedItem.selected;
    setSearchPayload(page);
  };

  useDidMountEffect(() => {
    debouncedSearchPayload();
    EventBus.emit(EventTypes.PAGE_CLEAR);
  }, [keyword]);

  useDidMountEffect(() => {
    if (selectedLocation) {
      handleClickLocationDialogOpen();
    } else {
      handleClickLocationDialogClose();
    }
  }, [selectedLocation]);

  useEffect(() => {
    setSearchPayload();
  }, []);

  return (
    <LocationViewContainer>
      <LocationViewTitle>장소관리</LocationViewTitle>
      <LocationViewHeaderContainer>
        <IconTextField
          placeholder={LOCATION_SEARCH_PLACEHOLDER}
          value={keyword}
          onChange={handleChangeTextField}
          width="27.063rem"
        />
        <ContainedYellowButton
          width="8.188rem"
          height="3.188rem"
          text={"신규 등록"}
          onClick={handleClickLocationDialogOpen}
        />
      </LocationViewHeaderContainer>
      <LocationListTable />
      <PageController basePage={locationPage} onPageChange={handleChangePage} />
      {locationDialogOpen && (
        <LocationFormDialog
          setIdPayload={setIdPayload}
          handleClickLocationDialogClose={handleClickLocationDialogClose}
        />
      )}
    </LocationViewContainer>
  );
};

export default LocationView;

const LocationViewContainer = styled.div`
  width: 61vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
`;

const LocationViewTitle = styled.div`
  padding: 1.5rem 0;
  color: ${theme.palette.primitives.black};
  font-size: 1.25rem;
  font-family: "Pretendard";
  font-style: normal;
  font-weight: 700;
  line-height: 1.5rem;
`;

const LocationViewHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`;
